/**
 * Settings Navigation Component
 * 
 * Left sidebar navigation menu for Settings sections
 * Following the design specifications for Settings expansion
 * 
 * Features:
 * - Active route highlighting
 * - Icon support for each menu item
 * - Keyboard navigation support
 * - Mobile-responsive (collapses to dropdown/tabs)
 * - Accessibility: ARIA labels, focus management
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { 
  User, 
  Settings as SettingsIcon, 
  FolderTree, 
  Bot, 
  Shield, 
  Bell, 
  Zap,
  ChevronDown
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const navItems: NavItem[] = [
  {
    path: '/settings/profile',
    label: 'Profile',
    icon: User,
    description: 'Personal information and profile settings'
  },
  {
    path: '/settings/account',
    label: 'Account Settings',
    icon: SettingsIcon,
    description: 'Language, currency, date format preferences'
  },
  {
    path: '/settings/categorisation',
    label: 'Categorisation',
    icon: FolderTree,
    description: 'Manage categories and tags'
  },
  {
    path: '/settings/ai',
    label: 'AI Configuration',
    icon: Bot,
    description: 'AI settings and preferences'
  },
  {
    path: '/settings/security',
    label: 'Security & Privacy',
    icon: Shield,
    description: 'Security settings, 2FA, sessions'
  },
  {
    path: '/settings/notifications',
    label: 'Notifications',
    icon: Bell,
    description: 'Notification preferences'
  },
  {
    path: '/settings/rules',
    label: 'Rules',
    icon: Zap,
    description: 'Transaction automation rules'
  },
];

export function SettingsNav(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasUnsavedChanges, onNavigationAttempt } = useNavigationGuard();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Find the active item
  const activeItem = navItems.find(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  ) || navItems[0];

  const handleSelectChange = (path: string) => {
    if (hasUnsavedChanges && location.pathname !== path) {
      onNavigationAttempt(path);
    } else {
      navigate(path);
    }
    setIsDropdownOpen(false);
  };

  // Close dropdown when route changes
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);

  return (
    <nav 
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
      aria-label="Settings navigation"
    >
      {/* Desktop Navigation (lg+) - Sidebar List */}
      <ul className="hidden lg:block space-y-2" role="list">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const handleClick = (e: React.MouseEvent) => {
            e.preventDefault();
            if (hasUnsavedChanges && !isActive) {
              onNavigationAttempt(item.path);
            } else {
              navigate(item.path);
            }
          };
          return (
            <li key={item.path} role="listitem">
              <button
                type="button"
                onClick={handleClick}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 text-left ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                aria-label={item.description}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Tablet & Mobile Navigation - Dropdown */}
      <div className="lg:hidden">
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            aria-label="Select settings section"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            <div className="flex items-center space-x-3">
              {activeItem && (() => {
                const ActiveIcon = activeItem.icon;
                return (
                  <>
                    <ActiveIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm font-medium">{activeItem.label}</span>
                  </>
                );
              })()}
            </div>
            <ChevronDown 
              className={`w-5 h-5 flex-shrink-0 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setIsDropdownOpen(false)}
                aria-hidden="true"
              />
              
              {/* Dropdown List */}
              <ul
                className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                role="listbox"
              >
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <li key={item.path} role="option" aria-selected={isActive}>
                      <button
                        type="button"
                        onClick={() => handleSelectChange(item.path)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors ${
                          isActive
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        aria-label={item.description}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

